import { useEffect, useRef, useState } from 'react';
import { Button, createFocusTrap } from '@alchemiesolns/ui';
import { convertNumbersToSubscripts } from '../utils/textFormatting';
import { useRecentlyUsed } from '../hooks/useRecentlyUsed';
import { RecentTextSection } from './RecentTextSection';
import { CharacterPalette } from './CharacterPalette';

interface TextInputDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Dialog title */
  title?: string;
  /** Initial text value */
  initialValue?: string;
  /** Placeholder text for the textarea */
  placeholder?: string;
  /** Callback when Save is clicked */
  onSave: (text: string) => void;
  /** Callback when Cancel is clicked or dialog is closed */
  onClose: () => void;
  /** Context for localStorage namespacing */
  context?: 'reactant' | 'product' | 'generic';
}

/**
 * A modal dialog for text input with Save/Cancel buttons.
 * Similar to ConfirmDialog but provides a textarea for user input.
 */
export function TextInputDialog({
  open,
  title = 'Add Text',
  initialValue = '',
  placeholder = 'Enter text...',
  onSave,
  onClose,
  context = 'generic',
}: TextInputDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState(initialValue);

  // Recently used hook with context-specific storage key
  const storageKey = `alchemie:recentText:${context}`;
  const { items, addItem, removeItem, clearAll } = useRecentlyUsed({
    storageKey,
    maxItems: 8,
    deduplicate: true
  });

  // Update text when initialValue changes
  useEffect(() => {
    setText(initialValue);
  }, [initialValue]);

  // Setup focus trap when dialog opens
  useEffect(() => {
    if (open && dialogRef.current) {
      const cleanup = createFocusTrap(dialogRef.current);
      // Focus the textarea when dialog opens
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
      return cleanup;
    }
  }, [open]);

  const handleSave = () => {
    onSave(text);
    addItem(text); // Add to recently used
    setText(''); // Reset after save
  };

  const handleSelectRecentItem = (itemText: string) => {
    setText(itemText);
    textareaRef.current?.focus();
  };

  const handleCharacterSelect = (char: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = text;

    // Insert character at cursor position
    const rawText = currentText.substring(0, start) + char + currentText.substring(end);
    const formattedText = convertNumbersToSubscripts(rawText);
    setText(formattedText);

    // Restore focus and cursor position after character insertion
    setTimeout(() => {
      textarea.focus();
      // Account for potential length change due to subscript conversion
      const newPosition = start + (formattedText.length - currentText.length + (end - start));
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const handleCancel = () => {
    setText(initialValue); // Reset to initial value
    onClose();
  };

  // Handle Escape key
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        handleCancel();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, initialValue]);

  if (!open) return null;

  return (
    <div className="confirm-dialog-overlay">
      <div
        ref={dialogRef}
        className="text-input-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="text-input-dialog-title"
      >
        <h3 id="text-input-dialog-title" className="text-input-dialog-title">
          {title}
        </h3>

        <RecentTextSection
          items={items}
          onSelectItem={handleSelectRecentItem}
          onDeleteItem={removeItem}
          onClearAll={clearAll}
        />

        <div style={{ marginBottom: '1rem' }}>
          <label
            className="text-sm font-medium text-gray-700"
            style={{
              fontSize: '0.75rem',
              color: 'rgb(75 85 99)',
              display: 'block',
              marginBottom: '0.5rem',
            }}
          >
            Special Characters
          </label>
          <CharacterPalette
            onCharacterSelect={handleCharacterSelect}
            showCopyFeedback={false}
          />
        </div>

        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(convertNumbersToSubscripts(e.target.value))}
          placeholder={placeholder}
          className="text-input-dialog-textarea"
          rows={1}
          aria-label="Text input"
        />

        <div className="text-input-dialog-buttons">
          <Button onClick={handleCancel}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
